import type { ReactElement } from "react";
import type { EventListGridItem } from "src/http/types";
import { useFormattedContent } from "src/shared/components/content";
import { useTeamThemes } from "src/website-apps/shared/hooks/useTeamThemes";
import { FavoriteTeamEventCard } from "./FavoriteTeamEventCard";
import { getMatchupOpponent, getTeamBranding } from "./utils";

interface FavoriteTeamCarouselItemProps {
  event: EventListGridItem;
  favoriteTeamCategoryId: number;
}

/**
 * Renders a single FavoriteTeamCarousel card. Calling useTeamThemes here (rather than in
 * FavoriteTeamCarousel's renderItem) keeps it a real per-event component, since renderItem
 * runs once per event and hooks can't be called from inside that callback.
 */
export const FavoriteTeamCarouselItem = ({
  event,
  favoriteTeamCategoryId,
}: FavoriteTeamCarouselItemProps): ReactElement => {
  const { homeTeamData, awayTeamData } = useTeamThemes(event.eventMetadata);
  const opponentTeamData = homeTeamData.categoryId === favoriteTeamCategoryId ? awayTeamData : homeTeamData;
  const opponent = getTeamBranding(opponentTeamData);
  const matchup = getMatchupOpponent(event.eventMetadata, favoriteTeamCategoryId);
  const homeMatchupLabel = useFormattedContent("cid$#4379484#", [matchup?.opponentName ?? ""]); // "vs {0}"
  const awayMatchupLabel = matchup ? `@ ${matchup.opponentName}` : "";
  const matchupLabel = (matchup?.isFavoriteTeamHome ? homeMatchupLabel : awayMatchupLabel) || event.name;
  // formattedDate alone omits the day of week (e.g. "Oct 25") — dayOfWeek is a separate field
  // that must be prepended, matching GridCardBase's eventDateWithDayOfWeek handling.
  const formattedDateWithDayOfWeek =
    event.dayOfWeek && event.formattedDate ? `${event.dayOfWeek}, ${event.formattedDate}` : event.formattedDate;
  const dateTimeText = [formattedDateWithDayOfWeek, event.formattedTime].filter(Boolean).join(" \u{2022} ");

  return (
    <FavoriteTeamEventCard
      logoUrl={opponent.logoUrl}
      teamImageUrl={opponent.teamImageUrl}
      imageAlt={matchupLabel}
      matchupLabel={matchupLabel}
      dateTimeText={dateTimeText}
      url={event.url ?? ""}
      fallbackAbbreviation={opponent.abbreviation}
      backgroundColor={opponent.backgroundColor}
    />
  );
};
